/**
 * NIGHTBOUND - the shelter.
 *
 * The room is always composited from separate layers so the creature is never
 * baked into its environment:
 *
 *   background -> structures -> furniture -> props -> [ creature ] -> foreground -> lighting
 *
 * Any layer may be missing; the scene degrades to a CSS-only room rather than
 * breaking. Dropping `public/assets/shelters/starter/shelter_lighting.webp` into
 * place is all it takes to add lighting art.
 */

import { useMemo, type ReactNode } from 'react'
import type { CreatureStateId } from '../../shared/creatureState'
import {
  STARTER_SHELTER_KEY,
  shelterLayerCandidates,
  type ShelterLayer,
} from '../../shared/assets/manifest'
import { useFirstAvailableAsset } from '../hooks'
import { cx } from './ui'

/** Layers drawn behind the creature, in order. */
const BEHIND: ShelterLayer[] = ['background', 'structures', 'furniture', 'props']
/** Layers drawn in front of the creature, in order. */
const FRONT: ShelterLayer[] = ['foreground']

export interface ShelterSceneProps {
  children?: ReactNode
  /** When SLEEPING the room darkens and the lamp settles. */
  state?: CreatureStateId
  className?: string
}

export function ShelterScene({ children, state, className }: ShelterSceneProps) {
  const dim = state === 'SLEEPING'

  return (
    <div
      className={cx(
        'nb-shelter relative isolate h-full w-full overflow-hidden rounded-[4px]',
        className,
      )}
    >
      {/* CSS room, always present, so the scene reads even with no art. */}
      <div aria-hidden className="absolute inset-0 nb-room" />

      {BEHIND.map((layer) => (
        <Layer key={layer} layer={layer} dim={dim} />
      ))}

      {/* Dust in the air - cheap atmosphere, pure CSS. */}
      <div aria-hidden className={cx('nb-dust absolute inset-0', dim && 'opacity-40')} />

      {/* The creature anchor: centred, standing on the floor line. */}
      <div className="absolute inset-x-0 bottom-[10%] top-[8%] flex items-end justify-center">
        <div className="h-full w-[68%] max-w-[520px] sm:w-[58%]">{children}</div>
      </div>

      {FRONT.map((layer) => (
        <Layer key={layer} layer={layer} dim={dim} />
      ))}

      {/* Lighting: art if present, otherwise a warm CSS glow. */}
      <Lighting dim={dim} />

      {/* Vignette keeps the centre readable regardless of art. */}
      <div aria-hidden className="nb-vignette absolute inset-0" />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-void/80 to-transparent"
      />
    </div>
  )
}

function Layer({ layer, dim }: { layer: ShelterLayer; dim: boolean }) {
  const candidates = useMemo(
    () => shelterLayerCandidates(STARTER_SHELTER_KEY, layer),
    [layer],
  )
  const { src } = useFirstAvailableAsset(candidates)
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={cx(
        'pointer-events-none absolute inset-0 h-full w-full select-none object-cover transition-opacity duration-1000',
        dim && layer === 'background' ? 'opacity-70' : 'opacity-100',
      )}
    />
  )
}

const LAMP_CANDIDATES = ['shelters/starter/shelter_lighting']

function Lighting({ dim }: { dim: boolean }) {
  const candidates = useMemo(() => shelterLayerCandidates(STARTER_SHELTER_KEY, 'lighting'), [])
  const { src } = useFirstAvailableAsset(candidates)
  void LAMP_CANDIDATES

  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        draggable={false}
        className={cx(
          'nb-flicker pointer-events-none absolute inset-0 h-full w-full select-none object-cover mix-blend-screen transition-opacity duration-1000',
          dim ? 'opacity-30' : 'opacity-100',
        )}
      />
    )
  }

  return (
    <div
      aria-hidden
      className={cx(
        'nb-lamp nb-flicker pointer-events-none absolute inset-0 transition-opacity duration-1000',
        dim ? 'opacity-25' : 'opacity-100',
      )}
    />
  )
}
